import type { CartItem, NormalizedOrderError, OrderingMenuItem, OrderingOptionGroup } from './types';
import type { ComboProfile } from '@/lib/menu/item-kinds/types';
import { parseTypeProfile } from '@/lib/menu/item-kinds';
import { resolveMaxSelect, resolveMinSelect } from '../modifier-limits';

/**
 * The guest's cart is scoped to the BRANCH — redesign doc 02 D5 / doc 03 §4.
 *
 * It used to be keyed on the menu id, which was fine only because a branch could have
 * exactly one menu. Doc 03 gives a branch several orderable lines (a fast-food menu and
 * a catering menu in one kitchen), and under the old key switching tabs would have
 * swapped the guest's cart out from under them — silently, with no error and no merge.
 * Lost revenue that nothing would ever have reported.
 *
 * Scoping to the branch is what makes "switching what you are browsing never changes
 * what you are holding" true by construction rather than by care.
 */
export function getCartStorageKey(branchId: string, session: { id: string } | null) {
  return session ? `mnrq:cart:${branchId}:${session.id}` : `mnrq:cart:${branchId}:public`;
}

/** The pre-redesign, menu-scoped key. Read once by the migration below, never written. */
export function getLegacyCartStorageKey(menuId: string, session: { id: string } | null) {
  return session ? `mnrq:service-cart:${menuId}:${session.id}` : `mnrq:service-cart:${menuId}:public`;
}

/**
 * Move a cart from the old menu-scoped key to the new branch-scoped one, once.
 *
 * A guest who filled a basket before the deploy and returned after it would otherwise
 * find it empty — which reads as "the site lost my order", not as "the key changed".
 * Runs before hydration reads the new key, is idempotent, and never overwrites a cart
 * that already exists under the new key.
 *
 * Safe to delete two releases after the cutover; until then it is the difference
 * between a silent migration and a support ticket.
 */
export function migrateLegacyCart(
  menuId: string,
  branchId: string,
  session: { id: string } | null,
): void {
  if (typeof window === 'undefined' || !menuId || !branchId) return;
  try {
    const target = getCartStorageKey(branchId, session);
    if (window.localStorage.getItem(target) !== null) return;
    const legacy = getLegacyCartStorageKey(menuId, session);
    const stored = window.localStorage.getItem(legacy);
    if (stored === null) return;
    window.localStorage.setItem(target, stored);
    window.localStorage.removeItem(legacy);
  } catch {
    // Private mode, blocked site data, quota. A cart that cannot be migrated is a
    // cart the guest re-adds; a throw here would blank the whole menu.
  }
}

// Money formatting lives in `lib/money.ts` , one formatter for the guest menu,
// the POS and the order tracker. Re-exported here so the many existing guest
// call sites keep working (D3, docs/qa/production-demo-readiness-2026-08-13.md).
export { currencySymbol, formatMoney } from '@/lib/money';
import { formatMoney as formatMoneyCanonical } from '@/lib/money';

export function formatOrderMoney(value: number, currency?: string) {
  return formatMoneyCanonical(value, currency);
}

export function getItemNumericPrice(item: Pick<OrderingMenuItem, 'price' | 'price_value'> | null | undefined) {
  if (!item) return 0;
  const dv = Number(item.price_value);
  if (Number.isFinite(dv)) return dv;
  const fv = Number(item.price);
  if (Number.isFinite(fv)) return fv;
  return 0;
}

export function formatItemPrice(item: Pick<OrderingMenuItem, 'price' | 'price_value' | 'currency' | 'pricing_mode' | 'price_per_unit_weight'> | null | undefined) {
  if (item?.pricing_mode === 'per_unit_weight' && item.price_per_unit_weight) {
    return formatOrderMoney(item.price_per_unit_weight, item?.currency);
  }
  return formatOrderMoney(getItemNumericPrice(item), item?.currency);
}

export function priceUnitSuffix(item: OrderingMenuItem): string | null {
  if (item.kind === 'package' && item.pricing_mode === 'per_person') return '/ person';
  if (item.kind === 'drink' && item.pricing_mode === 'per_pour') return '/ pour';
  if (item.pricing_mode === 'per_unit_weight') {
    const unit = item.weight_unit || 'kg';
    return `/ ${unit}`;
  }
  return null;
}

/**
 * The options a guest starts with when they open an item.
 *
 * Only FREE defaults are selected. A default that costs money is not honoured
 * here, because auto-selecting it charges the guest for a choice they never
 * made: opening the item silently raised the price shown on the sheet, and
 * add-to-cart then took that money for real. A merchant who wants a standard
 * choice included in the base price gives it a `price_delta` of 0 — at which
 * point selecting it costs the guest nothing and this filter keeps it.
 *
 * The consequence is deliberate and correct: a required group whose only option
 * costs money now prompts the guest to choose, instead of quietly billing them.
 */
export function buildDefaultSelection(groups: OrderingOptionGroup[]) {
  const sel: Array<{ optionGroupId: string; optionId: string }> = [];
  groups.forEach((g) =>
    (g.options || [])
      .filter((o) => o.is_default && !Number(o.price_delta))
      .forEach((o) => sel.push({ optionGroupId: g.id, optionId: o.id })),
  );
  return sel;
}

export function resolveDisplayPreferences(
  item: OrderingMenuItem,
  selectedOptions: Array<{ optionGroupId: string; optionId: string }>,
  freeTextPreferences: Array<{ label: string; value: string }>,
): CartItem['displayPreferences'] {
  const prefs: CartItem['displayPreferences'] = [];
  (item.option_groups || []).forEach((g) => {
    selectedOptions.forEach((sel) => {
      if (sel.optionGroupId !== g.id) return;
      const opt = (g.options || []).find((o) => o.id === sel.optionId);
      if (!opt) return;
      prefs.push({ label: g.name, value: opt.name, priceDelta: Number(opt.price_delta || 0) });
    });
  });
  freeTextPreferences.forEach((p) => prefs.push({ label: p.label, value: p.value, priceDelta: 0 }));
  return prefs;
}

export function validateSelection(item: OrderingMenuItem, selectedOptions: Array<{ optionGroupId: string; optionId: string }>) {
  const counts = new Map<string, number>();
  selectedOptions.forEach((s) => counts.set(s.optionGroupId, (counts.get(s.optionGroupId) || 0) + 1));
  for (const g of item.option_groups || []) {
    const count = counts.get(g.id) || 0;
    const min = resolveMinSelect(g);
    const max = resolveMaxSelect(g);
    if (count < min) return `Choose ${g.name.toLowerCase()} first.`;
    if (count > max) return `Too many options for ${g.name.toLowerCase()}.`;
  }
  return null;
}

export function getInvalidOptionGroupIds(item: OrderingMenuItem, selectedOptions: Array<{ optionGroupId: string; optionId: string }>) {
  const invalid = new Set<string>();
  const counts = new Map<string, number>();
  selectedOptions.forEach((s) => counts.set(s.optionGroupId, (counts.get(s.optionGroupId) || 0) + 1));
  for (const g of item.option_groups || []) {
    const count = counts.get(g.id) || 0;
    const min = resolveMinSelect(g);
    const max = resolveMaxSelect(g);
    if (count < min || count > max) invalid.add(g.id);
  }
  return invalid;
}

export function isOrderableItem(item: OrderingMenuItem | null | undefined) {
  if (!item) return false;
  if (item.available === false) return false;
  if (item.available_for_order === false) return false;
  return true;
}

export function getOrderingItemCategory(item: OrderingMenuItem) {
  return item.category || item.category_name || 'Menu items';
}

export function parseStoredCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is CartItem => Boolean(item && typeof item === 'object' && 'menuItemId' in item));
  } catch {
    // Corrupted cart data , return empty so the user starts fresh instead of
    // silently losing the cart with no explanation. The caller should clear
    // the storage key when this returns [].
    return [];
  }
}

/**
 * Turn an order-API failure into something worth showing a hungry guest.
 *
 * `status` matters: a coded 4xx carries a message written for the guest, and echoing
 * it is right. A 5xx does not , `internalApiError` returns the literal string
 * "Request failed.", and echoing *that* is how a guest at a table ended up staring at
 * two words that told them nothing. Anything 500 and above falls through to the
 * caller's friendly fallback instead.
 */
export function normalizeOrderError(
  payload: unknown,
  cart: CartItem[],
  fallbackMessage: string,
  status?: number,
): NormalizedOrderError {
  const record = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload as Record<string, unknown> : {};
  const code = typeof record.code === 'string' ? record.code.trim().toUpperCase() : '';
  const rawMessage = typeof (record.message || record.error) === 'string' ? String(record.message || record.error).trim() : '';
  const itemId = typeof record.item_id === 'string' ? record.item_id : typeof record.menu_item_id === 'string' ? record.menu_item_id : typeof record.itemId === 'string' ? record.itemId : '';
  const cartItem = itemId ? cart.find((i) => i.menuItemId === itemId || i.id === itemId) : null;
  const itemName = cartItem?.title || (typeof record.item_name === 'string' ? record.item_name : '') || 'that item';

  const cases: Record<string, NormalizedOrderError> = {
    INVENTORY_UNAVAILABLE: { message: `Sorry, ${itemName} is no longer available. Please remove it.`, itemIds: cartItem ? [cartItem.menuItemId] : [], retryable: false },
    ITEM_UNAVAILABLE: { message: `Sorry, ${itemName} is no longer available. Please remove it.`, itemIds: cartItem ? [cartItem.menuItemId] : [], retryable: false },
    ORDERING_DISABLED: { message: 'Online ordering is not available right now. Please ask staff for help.', itemIds: [], retryable: false },
    COUNTER_ORDERING_DISABLED: { message: 'Counter pickup ordering is not available right now. Please order at the counter.', itemIds: [], retryable: false },
    GUEST_NAME_REQUIRED: { message: 'Please add a pickup name before placing the order.', itemIds: [], retryable: false },
    SESSION_INVALID: { message: 'This table session could not be verified. Please scan the QR code again.', itemIds: [], retryable: false },
    SESSION_EXPIRED: { message: 'This table session has expired. Please scan the QR code again.', itemIds: [], retryable: false },
    MENU_BRANCH_MISMATCH: { message: 'This QR code belongs to a different branch. Please ask staff for help.', itemIds: [], retryable: false },
    OPTION_INVALID: { message: rawMessage || 'Check item options before placing the order.', itemIds: cartItem ? [cartItem.menuItemId] : [], retryable: false },
    OPTION_REQUIRED: { message: rawMessage || 'Check item options before placing the order.', itemIds: cartItem ? [cartItem.menuItemId] : [], retryable: false },
    RATE_LIMITED: { message: 'Too many attempts. Please wait a moment and try again.', itemIds: [], retryable: true },
    BRANCH_NOT_FOUND: { message: 'This menu is no longer active. Please scan the QR code again.', itemIds: [], retryable: false },
    INVALID_ENTRY_CODE: { message: 'Enter the access code shown at the counter. Ask staff if you do not have it.', itemIds: [], retryable: false },
    BRANCH_CLOSED: { message: 'This branch is currently closed. Please visit during operating hours.', itemIds: [], retryable: false },
    SHIFT_CLOSED: { message: 'Ordering is temporarily unavailable. Please ask staff for help.', itemIds: [], retryable: true },
    LOCATION_REQUIRED: { message: "Location access is needed to verify you're at the restaurant. Please enable location and try again.", itemIds: [], retryable: false },
    GUEST_OUT_OF_RANGE: { message: 'You appear to be outside the branch area. Please move closer or ask staff for help.', itemIds: [], retryable: false },
    // Audit P1: the slot the guest picked filled up or closed between the picker
    // and the submit. Retrying the same slot cannot succeed — the guest must
    // choose another time, so this is deliberately not retryable.
    SLOT_UNAVAILABLE: { message: 'That pickup time just filled up. Please choose another time.', itemIds: [], retryable: false },
  };

  if (cases[code]) return cases[code];
  if (rawMessage.toUpperCase().includes('UNAVAILABLE')) return cases.INVENTORY_UNAVAILABLE;

  // A server fault has no message fit for a guest , and it IS retryable, because the
  // guest did nothing wrong.
  if (typeof status === 'number' && status >= 500) {
    return { message: fallbackMessage, itemIds: [], retryable: true };
  }

  return { message: rawMessage || fallbackMessage, itemIds: [], retryable: true };
}

/** Parse combo profile from an ordering menu item's type_profile. */
export function getComboProfile(item: OrderingMenuItem): ComboProfile | null {
  if (item.kind !== 'combo') return null;
  const { profile } = parseTypeProfile('combo', item.type_profile);
  return (profile ?? null) as ComboProfile | null;
}

/** Validate that all combo slots have a selection. */
export function validateComboSelection(
  item: OrderingMenuItem,
  selections: Array<{ slotIndex: number; itemId: string }>,
): string | null {
  const profile = getComboProfile(item);
  if (!profile) return null;
  for (let i = 0; i < profile.components.length; i++) {
    const comp = profile.components[i];
    const count = selections.filter((s) => s.slotIndex === i && s.itemId).length;
    // CAT-03 (R20.7): require the FULL `included_qty`, not merely one.
    const includedQty = Math.max(1, Number(comp.included_qty || 1));
    if (count !== includedQty) {
      return `Choose ${includedQty} for ${String(comp.slot).toLowerCase()} first.`;
    }
  }
  return null;
}

/**
 * Pre-select each combo slot that the merchant gave a default.
 *
 * Slots with no `default_id` are deliberately left empty. Falling back to
 * `eligible_item_ids[0]` meant "choose your drink" silently answered itself
 * with whichever drink happened to sort first, and a guest who never looked at
 * that slot got a drink they did not pick , the one case the merchant was
 * signalling they did NOT want defaulted by leaving `default_id` null.
 * `validateComboSelection` then makes the guest fill the slot before adding.
 *
 * CAT-03 (R20.7 / R4.6): emits `included_qty` copies of the default so the
 * pre-filled cart already satisfies the server's exact-count requirement — the
 * two sides change together so existing carts are not rejected.
 */
export function buildDefaultComboSelections(item: OrderingMenuItem): Array<{ slotIndex: number; itemId: string }> {
  const profile = getComboProfile(item);
  if (!profile) return [];
  const selections: Array<{ slotIndex: number; itemId: string }> = [];
  profile.components.forEach((comp, i) => {
    const id = comp.default_id || '';
    if (!id) return;
    const qty = Math.max(1, Number(comp.included_qty || 1));
    for (let n = 0; n < qty; n += 1) selections.push({ slotIndex: i, itemId: id });
  });
  return selections;
}
